import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import OrderStatusCard from "../components/OrderStatusCard";
import ThemedBanner, { ORDER_CSS } from "../components/ThemedBanner";
import { endpoints } from "../apiConfig";
import { getTheme, themeVars } from "../onlineThemes";

// Statuses that still change on their own, so the page keeps checking for updates. Once an order is
// ready for pickup there is nothing more for the customer to wait on, so polling stops there.
const ACTIVE = ["awaiting_payment", "scheduled", "new", "preparing"];
const POLL_MS = 20000; // slow enough that a full order (well under an hour) stays under the server's lookup limit
const API = endpoints.onlinePlace.replace(/\/online\/place\.php$/, "");
const img = (p) => (p ? `${API}/${p}` : null);

// Public order tracking (by order number or phone number) in the restaurant's own theme.
export default function TrackPage() {
  const params = new URLSearchParams(window.location.search);
  const tenant = params.get("tenant") || "";
  const orderParam = params.get("order") || "";
  const savedPhone = sessionStorage.getItem(`remo_phone_${tenant}`) || "";
  const [query, setQuery] = useState(orderParam);
  const [active, setActive] = useState(orderParam ? (savedPhone || orderParam) : "");
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState(null);
  const [banner, setBanner] = useState(params.get("paid") ? { ok: true, text: "Payment received, thank you! Your order will appear below as soon as the restaurant confirms it." } : params.get("cancelled") ? { ok: false, text: "Payment cancelled. Your order isn't placed until it's paid. You can cancel it below or place a new order." } : null);
  const timer = useRef(null);
  const theme = useMemo(() => getTheme(brand?.branding?.theme), [brand]);

  useEffect(() => {
    if (!tenant) return;
    axios.get(endpoints.onlineMenu(tenant)).then((res) => setBrand(res.data)).catch(() => {});
  }, [tenant]);

  const lookup = useCallback(async (q, quiet) => {
    if (!q) return;
    if (!quiet) setLoading(true);
    try {
      const res = await axios.post(endpoints.onlineTrack, { tenant, query: q });
      let list = res.data.orders;
      if (orderParam) list = [...list.filter((o) => o.order_number === orderParam), ...list.filter((o) => o.order_number !== orderParam)];
      setOrders(list);
      setError("");
    } catch (err) {
      if (!quiet) { setOrders([]); setError(err.response?.data?.error || "Something went wrong. Please try again."); }
    }
    if (!quiet) setLoading(false);
  }, [tenant, orderParam]);

  useEffect(() => { if (active) lookup(active); }, [active, lookup]);
  useEffect(() => {
    clearInterval(timer.current);
    if (active && orders.some((o) => ACTIVE.includes(o.status))) timer.current = setInterval(() => lookup(active, true), POLL_MS);
    return () => clearInterval(timer.current);
  }, [active, orders, lookup]);

  const cancel = async (order) => {
    let phone = savedPhone;
    if (!phone) phone = window.prompt("To cancel, enter the phone number you used for this order:") || "";
    if (!phone) return;
    if (!window.confirm(`Cancel order ${order.order_number}?`)) return;
    try {
      await axios.post(endpoints.onlineTrack, { tenant, action: "cancel", order_number: order.order_number, phone });
      lookup(active, true);
    } catch (err) {
      window.alert(err.response?.data?.error || "Couldn't cancel that order.");
    }
  };
  const submit = (e) => { e.preventDefault(); setBanner(null); setActive(query.trim()); if (query.trim() === active) lookup(query.trim()); };
  const br = brand?.branding;

  return (
    <div className="op-root" style={themeVars(theme)}>
      <style>{ORDER_CSS}</style>
      <div style={{ maxWidth: 560, margin: "0 auto" }}><ThemedBanner theme={theme} bannerUrl={img(br?.banner)} logoUrl={img(br?.logo)} initials={br?.initials || ""} compact /></div>
      <div className="op-wrap" style={{ maxWidth: 560 }}>
        <header className="op-head" style={{ paddingTop: 42 }}>
          {brand && <div style={{ fontFamily: "var(--hf)", fontWeight: "var(--hw)", fontSize: 18, color: "var(--mu)" }}>{brand.business_name}</div>}
          <h1 className="op-name" style={{ fontSize: 26 }}>Track your order</h1>
        </header>
        {banner && <div className="op-notice" style={{ background: banner.ok ? "#ecfdf3" : "#fffaeb", color: banner.ok ? "#067647" : "#93370d" }} role="status" data-testid="track-banner">{banner.text}</div>}
        <form onSubmit={submit} style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
          <input className="op-field" style={{ flex: "1 1 220px", marginBottom: 0 }} placeholder="Order number (K7M-0142) or phone number" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Order number or phone number" />
          <button className="op-btn" type="submit" disabled={loading}>{loading ? "Looking…" : "Find my order"}</button>
        </form>
        <p className="op-hint" style={{ margin: "8px 0 14px" }}>Using your phone number shows your orders from the last 24 hours.</p>
        {error && <div className="op-err" role="alert">{error}</div>}
        {orders.map((o, i) => <OrderStatusCard key={o.order_number} order={o} onCancel={cancel} highlight={i === 0 && !!orderParam} />)}
        {tenant && <a href={`/order?tenant=${tenant}`} style={{ color: "var(--ac)", fontSize: 14 }}>← Back to the menu</a>}
        <p className="op-powered">Powered by Alvna</p>
      </div>
    </div>
  );
}
